import { IYearMonth } from '@/coreDomainsModule/date';

export const DashboardAlertSeverities = ['critical', 'warning'] as const;
/**
 * アラートの警戒度
 */
export type DashboardAlertSeverity = (typeof DashboardAlertSeverities)[number];

export const DashboardAlertDirections = ['excess', 'shortage'] as const;
/**
 * アラートの向き
 */
export type DashboardAlertDirection = (typeof DashboardAlertDirections)[number];

const DashboardAlertSettingsValueMandatoryTypes = [
  'consecutiveMonths',
  'ratioThreshold',
  'momChangeRateThreshold',
  'minQuantityThreshold',
] as const;
/**
 * 基本アラート設定種類のタイプ
 */
type DashboardAlertSettingsValueMandatoryType = (typeof DashboardAlertSettingsValueMandatoryTypes)[number];

const DashboardAlertSettingsValueOptionalTypes = ['currentMonthForecastAccuracyThreshold'] as const;
/**
 * 当月アラート設定種類のタイプ
 */
type DashboardAlertSettingsValueOptionalType = (typeof DashboardAlertSettingsValueOptionalTypes)[number];

export const DashboardAlertSettingValueTypes = [
  ...DashboardAlertSettingsValueMandatoryTypes,
  ...DashboardAlertSettingsValueOptionalTypes,
] as const;
/**
 * アラート設定種類のタイプ
 */
export type DashboardAlertSettingValueType = (typeof DashboardAlertSettingValueTypes)[number];

/**
 * ダッシュボードのキーごとの設定値
 */
export type DashboardAlertSettingInKey = Record<DashboardAlertSettingsValueMandatoryType, string> &
  Partial<Record<DashboardAlertSettingsValueOptionalType, string>>;

/**
 * ダッシュボードの警戒度ごとの設定値
 */
export type DashboardAlertSettingInSeverity = Record<DashboardAlertDirection, DashboardAlertSettingInKey>;

/**
 * ダッシュボードアラート設定のType
 */
export type DashboardAlertSetting = Record<DashboardAlertSeverity, DashboardAlertSettingInSeverity>;

/**
 * 設定変更のinputバリデーション
 * @param severity - 重大度
 * @param direction - 向き
 * @param value - 値
 * @param key - イベントが発生したinputのキー
 */
export const dashboardAlertSettingValidation = (
  severity: DashboardAlertSeverity,
  direction: DashboardAlertDirection,
  value: string | undefined,
  key: DashboardAlertSettingValueType
): string => {
  if (!(severity === 'critical' && direction === 'shortage') && key === 'currentMonthForecastAccuracyThreshold' && !value) return '';

  if (!value) {
    return '値を入力してください';
  }
  const x = Number(value);
  if (!Number.isFinite(x)) {
    return '不正な値が入力されています。';
  }

  switch (key) {
    case 'consecutiveMonths':
      if (!Number.isSafeInteger(x) || x < 0) {
        return '月は0以上の整数で入力してください。';
      }
      break;
    case 'ratioThreshold':
    case 'momChangeRateThreshold':
    case 'currentMonthForecastAccuracyThreshold':
      if (x >= 10000 || x < 0) {
        return 'パーセンテージは0以上10000より小さい範囲で入力してください。';
      }
      if ((value.split('.')[1] || '').length > 2) {
        return '小数点以下は2桁以内で入力してください。';
      }
      break;
    case 'minQuantityThreshold':
      if (x < 0) {
        return '数量は0以上の数値で入力してください。';
      }
      if ((value.split('.')[0] || '').length > 25 || (value.split('.')[1] || '').length > 6) {
        return '数量が入力できる制限を超えています。';
      }
  }
  return '';
};

export const DEFAULT_DASHBOARD_ALERT_SETTING: DashboardAlertSetting = {
  critical: {
    excess: {
      consecutiveMonths: '2',
      ratioThreshold: '50',
      momChangeRateThreshold: '50',
      minQuantityThreshold: '100',
    },
    shortage: {
      consecutiveMonths: '2',
      ratioThreshold: '150',
      momChangeRateThreshold: '200',
      minQuantityThreshold: '100',
      currentMonthForecastAccuracyThreshold: '100',
    },
  },
  warning: {
    excess: {
      consecutiveMonths: '2',
      ratioThreshold: '80',
      momChangeRateThreshold: '75',
      minQuantityThreshold: '100',
    },
    shortage: {
      consecutiveMonths: '2',
      ratioThreshold: '120',
      momChangeRateThreshold: '125',
      minQuantityThreshold: '100',
    },
  },
};

export const DashboardAlertTypes = [
  'excess_forecast_sharp_decrease',
  'excess_forecast_consecutive_low',
  'shortage_forecast_sharp_increase',
  'shortage_forecast_consecutive_high',
  'shortage_forecast_current_high',
  'shortage_out_of_stock',
] as const;

/**
 * b
 */
export type DashboardAlertType = (typeof DashboardAlertTypes)[number];

/**
 * a
 */
export type DashboardAlert = {
  kind: DashboardAlertType;
  reason: DashboardAlertReason;
};

/**
 * a
 */
export type DashboardAlertReason = DashboardAlertReasonCf | DashboardAlertReasonCfAndSr | undefined;

/**
 * a
 */
type DashboardAlertReasonBase = {
  targetYearMonth: IYearMonth;
};

/**
 * a
 */
type DashboardAlertReasonCf = DashboardAlertReasonBase & {
  latestCustomerForecastAmount: number | undefined;
  previousCustomerForecastAmount: number | undefined;
};

/**
 * a
 */
type DashboardAlertReasonCfAndSr = DashboardAlertReasonBase & {
  salesResultAmount: number | undefined;
  customerForecastAmount: number | undefined;
};
